import { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eraser, PenLine } from "lucide-react";

// Canvas drawn e-signature. Emits a PNG data URL via onChange when the stroke ends.
export default function SignaturePad({ value, onChange, label, testid }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [editing, setEditing] = useState(!value);

  useEffect(() => {
    if (!editing) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const down = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
    const up = () => { if (drawing.current) { drawing.current = false; onChange(c.toDataURL("image/png")); } };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", up);
    };
  }, [editing, onChange]);

  const clear = () => {
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange("");
  };

  return (
    <div data-testid={testid}>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      {!editing && value ? (
        <div className="mt-1.5 border border-slate-200 rounded-lg bg-white p-1 flex items-center justify-between">
          <img src={value} alt={label} className="h-16 object-contain" data-testid={`${testid}-image`} />
          <Button size="sm" variant="ghost" className="text-slate-400 text-xs" onClick={() => { setEditing(true); }} data-testid={`${testid}-resign`}><PenLine className="h-3.5 w-3.5 mr-1" /> Re-sign</Button>
        </div>
      ) : (
        <div className="mt-1.5">
          <canvas ref={canvasRef} width={320} height={80} className="w-full border border-dashed border-slate-300 rounded-lg bg-white touch-none cursor-crosshair" data-testid={`${testid}-canvas`} />
          <Button size="sm" variant="ghost" className="text-slate-400 text-xs mt-1" onClick={clear} data-testid={`${testid}-clear`}><Eraser className="h-3.5 w-3.5 mr-1" /> Clear</Button>
        </div>
      )}
    </div>
  );
}
